import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
    FormioCustomEventComponent,
    handleStartProcessCustomEvent,
} from 'src/components/EntityPresentation/utils/handleStartProcessCustomEvent';
import { NotFound } from 'src/components/Errors/NotFound';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { Router } from 'src/core/router/Router';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';

export type EntityDetailsProps = {
    store: BasicStore<any>;
};
export const EntityDetails = observer(({ store }: EntityDetailsProps) => {
    const { params, url } = useRouteMatch<{ entityId: string }>();
    const { entityId } = params;

    useEffect(() => {
        if (!entityId) {
            return;
        }
        store.loadItem(entityId);
        store.loadForm(store.itemDetailsFormKey);

        return () => {
            store.formLoader.reset();
            store.itemLoader.reset();
        };
    }, [entityId]);

    const loadingStatus = useMemo(() => {
        return new CombinedLoadingStatus([store.formLoader, store.itemLoader]);
    }, []);

    if (loadingStatus.isLoading) {
        return <SpinnerFullWidth />;
    }

    if (store.itemLoader?.errorData?.status === 404) {
        return <NotFound />;
    }

    return (
        <div>
            <FormioForm
                key={`${entityId}-details`}
                form={store.currentItemForm as any}
                submission={{ data: store.currentItem } as any}
                onCustomEvent={(event) => {
                    if (event.type === 'startProcess') {
                        handleStartProcessCustomEvent(event, url);
                    }

                    if (event.type === 'edit') {
                        Router.navigate(`${url}/edit`);
                    }

                    if (event.type === 'create') {
                        const entity = (
                            event.component as FormioCustomEventComponent
                        ).properties?.entity;
                        const queryParams = (
                            event.component as FormioCustomEventComponent
                        ).properties?.urlParams;
                        Router.navigate(`/${entity}/create?${queryParams}`);
                    }

                    return Promise.resolve();
                }}
            />
        </div>
    );
});
